import { AnimatePresence, motion } from 'framer-motion';
import React, { act, useContext, useEffect, useState } from 'react';
import { Alert, Button, Dropdown, Form as RBForm, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import {
  FaArrowRight,
  FaArrowLeft,
  FaArrowsAltH,
  FaCheckCircle,
  FaEnvelope,
  FaLongArrowAltLeft,
  FaLongArrowAltRight,
  FaPaperPlane,
  FaRobot,
  FaUserPlus,
  FaUserShield
} from 'react-icons/fa';
import { BsEnvelopeArrowDownFill } from "react-icons/bs";
import { UserRequestFlow, UserRequestFlowStepType } from './_core/_model.ts';
import { GetAllowedFlows, UpsertActiveFlow } from './_core/_request.ts';
import SavingIndicator from '../../UIComponents/SavingIndicator.tsx';
import { ThemeContext } from '../../context/ThemeContext.js';
import { AreObjectsEquals, GetObjectDifferences } from '../../Utilities/Utilities.ts';
import { AuthContext } from '../../context/AuthContext.js';

const typeIcons = {
  [UserRequestFlowStepType.request]: <FaUserPlus />,
  [UserRequestFlowStepType.approveApprover]: <FaUserShield />,
  [UserRequestFlowStepType.approveAdmin]: <FaUserShield />,
  [UserRequestFlowStepType.createAdmin]: <FaCheckCircle />,
  [UserRequestFlowStepType.createAuto]: <FaRobot />
}

const tempFlows = [
  {
    id: 1,
    label: 'Request → Manager Approval → Admin Creates',
    description: "Request is submitted by user, manager approves it, then admin creates the user. Ideal for strict approval processes.",
    steps: [
      {
        label: 'Request', stepType: UserRequestFlowStepType.request, sendMail: true,
        mailTo: ['Receiver', 'Admin']
      },
      {
        label: 'Manager Approval', stepType: UserRequestFlowStepType.approveApprover,
        sendMail: true,
        mailTo: ['Sender', 'Admin']
      },
      {
        label: 'Admin Creates', stepType: UserRequestFlowStepType.createAdmin,
        sendMail: true,
        mailTo: ['Sender', 'Receiver']
      }
    ],
    isActive: true
  },
  {
    id: 2,
    label: 'Request → Manager Approval (Optional) → Admin Creates',
    description: "Similar to Flow 1, but manager approval is optional. Use when approvals are conditional.",
    steps: [
      { label: 'Request', stepType: UserRequestFlowStepType.request, sendMail: false, mailTo: [] },
      { label: 'Manager Approval (Optional)', stepType: UserRequestFlowStepType.approveApprover, sendMail: false, mailTo: [] },
      { label: 'Admin Creates', stepType: UserRequestFlowStepType.createAdmin, sendMail: false, mailTo: [] }
    ],
    isActive: false
  },
  {
    id: 3,
    label: 'Request → Admin Creates',
    description: "Only request and admin creation steps. Best for small teams or quick onboarding.",
    steps: [
      { label: 'Request', stepType: UserRequestFlowStepType.request, sendMail: false, mailTo: [] },
      { label: 'Admin Creates', stepType: UserRequestFlowStepType.createAdmin, sendMail: false, mailTo: [] }
    ],
    isActive: false
  },
  {
    id: 4,
    label: 'Request → Create Users Automatically',
    description: "User is created automatically after request, without human approval. Fast and fully automated.",
    steps: [
      { label: 'Request', stepType: UserRequestFlowStepType.request, sendMail: false, mailTo: [] },
      { label: 'Create Users Automatically', stepType: UserRequestFlowStepType.createAuto, sendMail: false, mailTo: [] }
    ],
    isActive: false
  }
];

export default function UserCreationFlows() {
  const [alloweFlows, setAllowedFlows] = useState<UserRequestFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [initialFlows, setInitialFlows] = useState<UserRequestFlow[]>([]);
  const { themeConfig } = useContext(ThemeContext);
  const [progress, setProgress] = useState<number>(0);
  const { user: loggedInUser } = useContext(AuthContext);

  const initlaize = () => {
    setLoading(true);
    GetAllowedFlows().then((x) => {
      setAllowedFlows([...x.data]);
      let tval = JSON.parse(JSON.stringify(x.data));
      setInitialFlows([...tval]);
      setLoading(false);
    }).catch(() => {
      setAllowedFlows([]);
      setInitialFlows([]);
      setLoading(false);
    })
  }
  useEffect(() => {
    initlaize();
  }, [])

  const emailExplanation = "Note: If 'Send Email Notification' is enabled for a step, selected stakeholders will receive an email when that step is executed. For example, if 'Receiver' is checked for the 'Request' step, the receiver will get an email when the request is submitted. The content of these emails can be customized under Settings → Email Config Options.";

  const activeFlow = alloweFlows[currentTab];

  const saveItem = () => {
    setProgress(1);
    let nVal = alloweFlows;
    for (var indx in alloweFlows) {
      let dFields = GetObjectDifferences(alloweFlows[indx], initialFlows[indx]);
      if (dFields.length > 0) {
        nVal[indx].dirtyFields = dFields;
        nVal[indx].byUser = loggedInUser.userID;
      }
    }
    UpsertActiveFlow(nVal).then((x) => {
      setInitialFlows([...alloweFlows]);
      setProgress(0);
    }).catch(() => {
      initlaize();
      setTimeout(() => {
        setProgress(0);
      }, 5000);
    })
  }

  const resetToInitial = () => {
    setAllowedFlows([...initialFlows]);
  }
  return (
    loading ?
      <div>
        {/* Placeholder for Tabs */}
        <div className="mb-3 d-flex flex-wrap gap-2">
          {[1, 2, 3, 4].map((_, idx) => (
            <span key={idx} className="placeholder col-2 py-2 rounded bg-secondary" style={{ height: '38px' }}></span>
          ))}
        </div>

        {/* Placeholder for Description and Email Text */}
        <div className="mb-2 placeholder-glow">
          <span className="placeholder col-6"></span>
        </div>
        <div className="mb-3 placeholder-glow">
          <span className="placeholder col-10"></span>
        </div>

        {/* Placeholder for Step Cards */}
        <div className="d-flex gap-3 flex-wrap">
          {[1, 2, 3].map((_, i) => (
            <div
              key={i}
              className="p-3 bg-light border rounded"
              style={{ minWidth: 260 }}
            >
              <div className="placeholder-glow mb-2">
                <span className="placeholder col-6"></span>
              </div>
              <div className="placeholder-glow mb-2">
                <span className="placeholder col-8"></span>
              </div>
              <div className="placeholder-glow mb-2">
                <span className="placeholder col-10"></span>
              </div>
              <div className="placeholder-glow">
                <span className="placeholder col-4"></span>
              </div>
            </div>
          ))}
        </div>
      </div> :
      <div>
        <div className='d-inline-flex flex-column'>
          <label className='fw-normal text-secondary' style={{ fontSize: "12px" }}>Choose a flow for user creation:</label>
          <p className="text-muted-theme small mb-2">
            Each flow defines how users are added to the system. Select a flow and customize who should receive email notifications at each step.
          </p>
          <ToggleButtonGroup
            type="radio"
            name="flowOptions"
            value={currentTab}
            onChange={(val) => {
              setCurrentTab(val);
            }}
            className="mb-1 j_st_usCr_f "
          >
            {alloweFlows.map((f, indx) => (
              <ToggleButton
                key={indx}
                id={`flow-${f.id}`}
                variant={(currentTab == indx) ? 'success' : 'outline-secondary'}
                className={`px-3 py-2 d-flex align-items-center gap-2 justify-content-center text-center ${(currentTab == indx) ? 'active shadow-sm active-flow-glow' : 'text-default'}`}
                value={indx}
              >
                {`Flow ${f.id}`}
                {f.isActive && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="d-flex align-items-center gap-1"
                  >
                    <FaCheckCircle className="" />
                    <motion.div
                      animate={{
                        x: [0, 5, 0],
                        opacity: [0.8, 1, 0.8],
                      }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <FaArrowRight className="" />
                    </motion.div>
                  </motion.div>
                )}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>


        <div className='d-flex flex-row align-items-center justify-content-between mb-3' style={{ height: "30px" }}>
          <p className="text-muted-theme small m-0">{activeFlow.description}</p>
          {!activeFlow.isActive && (
            <div className="text-end">
              <Button
                variant="outline-success"
              className='j_f_btn_ac'
                size="sm"
                onClick={() => {
                  const updated = alloweFlows.map(x => ({
                    ...x,
                    isActive: (x.id === alloweFlows[currentTab].id),
                  }));
                  setAllowedFlows([...updated]);
                }}
              >
                <FaArrowRight className="me-1" />
                Make This Flow Active
              </Button>
            </div>
          )}
        </div>
        <p className="text-muted-theme small fst-italic">{emailExplanation}</p>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFlow.id}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
          className="d-flex align-items-center flex-wrap gap-0  j_f_at_step"
          >
            {activeFlow.steps.map((step, index) => {
              return (
                <motion.div
                  key={index}
                  className="d-flex align-items-center position-relative"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }}
                >
                  <motion.div
                   className={`d-flex flex-column align-items-center text-center p-3 bg-theme-card rounded border ${
                    step.label === "Request"
                      ? "j_f_st_req"
                      : step.label === "Manager Approval (Optional)"
                      ? "j_st_mngr"
                      : step.label ===  "Admin Creates"
                      ? "j_st_admin"
                      : ""
                  }`}
                    style={{ minWidth: 260 }}
                  >
                    <div className="fs-3 text-primary mb-2">{typeIcons[step.stepType]}</div>
                    <div className="fw-semibold mb-2">{step.label}</div>

                    {/* Email Settings Grouped */}
                    <div className="w-100 px-2 py-2 mt-2 border rounded bg-theme-card text-center">
                    <div className="form-check d-flex align-items-center justify-content-center gap-2 mb-2 j_st_email">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`mail-${index}`}
                          checked={step.sendMail}
                          onChange={(e) => {
                            let aF = alloweFlows;
                            aF[currentTab].steps[index].sendMail = e.target.checked;
                            setAllowedFlows([...aF]);
                          }}
                        />
                        <label htmlFor={`mail-${index}`} className="form-check-label d-flex align-items-center gap-1 mt-1">
                          <FaEnvelope className="text-muted-theme" />
                          <span>Send Email Notification</span>
                        </label>
                      </div>

                      {step.mailTo && (
                        <>
                        <Dropdown autoClose="outside" className="mb-2 w-100 j_st_hld">
                            <Dropdown.Toggle size="sm" variant="outline-secondary" className="w-100 text-start">
                              {step.mailTo.length > 0
                                ? step.mailTo.join(', ')
                                : 'Select Stakeholder(s)'}
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                              style={{
                                minWidth: '200px',
                                maxHeight: '180px',
                                overflowY: 'auto',
                                padding: '0.5rem'
                              }}
                            >
                              {['Sender', 'Receiver', 'Admin'].map(role => {
                                const inputId = `${step.label}-${role}`;
                                return (
                                  <div key={role} className="form-check mb-2 d-flex flex-row align-items-center gap-2">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={inputId}
                                      checked={step.mailTo.includes(role)}
                                      onChange={() => {
                                        const updated = step.mailTo.includes(role)
                                          ? step.mailTo.filter(r => r !== role)
                                          : [...step.mailTo, role];
                                        let aF = alloweFlows;
                                        aF[currentTab].steps[index].mailTo = [...updated];
                                        setAllowedFlows([...aF]);
                                      }}
                                    />
                                    <label htmlFor={inputId} className="form-check-label">
                                      {role}
                                    </label>
                                  </div>
                                );
                              })}
                            </Dropdown.Menu>
                          </Dropdown>
                          <div className="d-flex flex-wrap gap-1 justify-content-center">
                            {step.mailTo.map(role => (
                              <span className="badge bg-theme-card border text-dark d-flex align-items-center gap-1" key={role}>
                                {role === 'Sender' && <FaPaperPlane className="text-primary" />}
                                {role === 'Receiver' && <BsEnvelopeArrowDownFill className="text-success" />}
                                {role === 'Admin' && <FaUserShield className="text-danger" />}
                                {role}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>

                  {index < activeFlow.steps.length - 1 && (
                    <div className="d-flex align-items-center ms-1 me-1" style={{ minWidth: 50 }}>
                      <motion.div
                        className="flex-grow-1"
                        style={{
                          height: 2,
                          background: 'repeating-linear-gradient(to right, #0d6efd 0 6px, transparent 6px 12px)'
                        }}
                        animate={{ backgroundPositionX: ['0%', '100%'] }}
                        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                      />
                      <FaArrowRight className="ms-2 text-primary" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence>
          {progress != 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              style={{
                position: "fixed",
                bottom: 20,
                left: 0,
                right: 0,
                margin: "0 auto",
                zIndex: 1055,
                width: "fit-content",
                maxWidth: "90%",
                pointerEvents: "none", // Optional: allows clicks to pass through
              }}
            >
              <Alert variant={progress > 0 ? "success" : "warning"} className="d-flex align-items-center gap-2 shadow-lg px-3 py-2 mb-0">
                {
                  progress > 0 ?
                    <SavingIndicator
                      {...{
                        color: themeConfig.colors.primary,
                        style: "spinner",
                        message: (progress > 0) ? "Saving User flow" : "There seems some error",
                        type: "saving"
                      }}
                    /> : "There seems some error"
                }

              </Alert>
            </motion.div>
          ) :
            <div className="mt-4 text-end">
            <Button variant="secondary" onClick={resetToInitial} className="me-2 j_st_rstBtn">Reset</Button>
              <Button variant="success" className="me-2 j_st_svBtn" onClick={saveItem}>
                {

                }
                Save
              </Button>
            </div>
          }
        </AnimatePresence>
      </div>
  );
}
